import React, { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import TagInputWidget from '../common/apexo/TagInputWidget';
import { useI18n } from '../../i18n/I18nProvider';

interface CareFlow360HeaderProps {
  patientName?: string;
  patientId?: string;
  visitTags: string[];
  setVisitTags: React.Dispatch<React.SetStateAction<string[]>>;
  tagsSyncState: 'idle' | 'saving' | 'conflict' | 'error';
  tagsUpdatedAt: string | null;
  visitVersion: number;
  streamUpdatedAt: string | null;
  liveStatus: 'connecting' | 'open' | 'closed' | 'error' | 'unauthenticated';
  viewMode: 'timeline' | 'kanban';
  setViewMode: React.Dispatch<React.SetStateAction<'timeline' | 'kanban'>>;
  formatDateTime: (_value?: string | null) => string;
  normalizeTags: (_value: string[]) => string[];
  dashboardStats: {
    appointmentsToday: number;
    newPatientsToday: number;
    paymentsToday: number;
  };
}

const liveStatusDot: Record<string, string> = {
  open: '#10b981',
  connecting: '#f59e0b',
  closed: '#94a3b8',
  error: '#ef4444',
  unauthenticated: '#f59e0b',
};

export default function CareFlow360Header({
  visitTags,
  setVisitTags,
  tagsSyncState,
  tagsUpdatedAt,
  visitVersion,
  streamUpdatedAt,
  liveStatus,
  viewMode,
  setViewMode,
  formatDateTime,
  normalizeTags,
  patientName,
  patientId,
  dashboardStats,
}: CareFlow360HeaderProps) {
  const { t } = useI18n();
  const [showQr, setShowQr] = useState(false);
  const paymentsDisplay = dashboardStats.paymentsToday.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const syncLabel = tagsSyncState === 'saving' ? '✨ Saving…'
    : tagsSyncState === 'conflict' ? '⚠ Conflict'
    : tagsSyncState === 'error' ? '✗ Error'
    : tagsUpdatedAt ? `✓ Synced`
    : '';

  return (
    <div className="cf360-header cf360-header-compact">
      {/* ── Top bar: brand + stats + controls ── */}
      <div className="cf360-header-topbar">
        <div className="cf360-header-brand">
          <span className="cf360-header-dot" style={{ background: liveStatusDot[liveStatus] || '#94a3b8' }} title={`Live: ${liveStatus}`} />
          <span className="cf360-header-title">
            {patientName ? (
              <><strong>{patientName}</strong><span className="cf360-header-sub">· v{visitVersion}</span></>
            ) : (
              <strong>CareFlow 360</strong>
            )}
          </span>
        </div>

        {/* Mini stats chips */}
        <div className="cf360-header-stats">
          <span className="cf360-stat-chip cf360-stat-purple" title="Appointments Today">
            📅 {dashboardStats.appointmentsToday}
          </span>
          <span className="cf360-stat-chip cf360-stat-blue" title="Patients Today">
            👥 {dashboardStats.newPatientsToday}
          </span>
          {dashboardStats.paymentsToday > 0 && (
            <span className="cf360-stat-chip cf360-stat-teal" title="Payments Today">
              ₹{paymentsDisplay}
            </span>
          )}
        </div>

        {/* Right controls */}
        <div className="cf360-header-controls">
          {/* View toggle: compact pill */}
          <div className="cf360-view-toggle">
            <button
              className={viewMode === 'timeline' ? 'active' : ''}
              onClick={() => setViewMode('timeline')}
              title="Timeline view"
            >
              📋 Timeline
            </button>
            <button
              className={viewMode === 'kanban' ? 'active' : ''}
              onClick={() => setViewMode('kanban')}
              title="Kanban view"
            >
              🗂 Kanban
            </button>
          </div>

          {patientId && (
            <button
              className="cf360-qr-btn"
              type="button"
              onClick={() => setShowQr(true)}
              title="Show patient QR code"
            >
              🔳
            </button>
          )}
        </div>
      </div>

      {/* ── Tag input bar ── */}
      <div className="cf360-header-tagrow">
        <div className="cf360-tagrow-input">
          <TagInputWidget
            tags={visitTags}
            onChange={(tags) => setVisitTags(normalizeTags(tags))}
            suggestions={['Urgent', 'Review Needed', 'Completed', 'High Priority', 'Follow-up', 'Aesthetic']}
            placeholder={t('careflow_tags_placeholder')}
          />
        </div>
        {syncLabel && (
          <span className={`cf360-sync-label cf360-sync-${tagsSyncState}`}>{syncLabel}</span>
        )}
      </div>

      {/* QR Modal */}
      {showQr && patientId && (
        <div className="cf360-qr-modal" onClick={() => setShowQr(false)}>
          <div className="cf360-qr-card" onClick={(e) => e.stopPropagation()}>
            <h3>{t('careflow_qr_title')}</h3>
            <QRCodeCanvas value={`TD:${patientId}`} size={180} />
            <div className="cf360-qr-id">Patient ID: {patientId}</div>
            <button className="cf360-qr-close" type="button" onClick={() => setShowQr(false)}>
              {t('careflow_qr_close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
